import { Element } from './element';
import { EntityTransformHandler } from './entity-transform-handler';
import { Events } from './events';
import { Mesh } from './mesh';
import { registerPivotEvents } from './pivot';
import { Splat } from './splat';
import { SplatsTransformHandler } from './splats-transform-handler';

interface TransformHandler {
    activate: () => void;
    deactivate: () => void;
}

const registerTransformHandlerEvents = (events: Events) => {
    const existingDispose = (events as any)._transformHandlersDispose as undefined | (() => void);
    if (existingDispose) {
        return existingDispose;
    }

    const transformHandlers: TransformHandler[] = [];

    const push = (handler: TransformHandler) => {
        if (transformHandlers.length > 0) {
            const transformHandler = transformHandlers[transformHandlers.length - 1];
            transformHandler.deactivate();
        }
        transformHandlers.push(handler);
        handler.activate();
    };

    const pop = () => {
        if (transformHandlers.length > 0) {
            const transformHandler = transformHandlers.pop();
            transformHandler.deactivate();
        }
        if (transformHandlers.length > 0) {
            const transformHandler = transformHandlers[transformHandlers.length - 1];
            transformHandler.activate();
        }
    };

    // Pivot must exist before any handler tries to read it.
    registerPivotEvents(events);

    // bind transform target when selection changes
    const entityTransformHandler = new EntityTransformHandler(events);
    const splatsTransformHandler = new SplatsTransformHandler(events);

    const update = (selection: Splat | Mesh | Element) => {
        // CRITICAL: Don't swap handlers during an active pivot manipulation to prevent data loss.
        const pivot = events.invoke('pivot') as any;
        if (pivot?.active) {
            // In practice, lost pointerup can leave pivot.active stuck true. If we don't recover,
            // selection changes can require a click-off/click-on ritual before transforms apply.
            try {
                pivot.end?.();
            } catch (e) {
                // best effort
            }
        }

        pop();
        if (selection) {
            // Vehicle-motion animated helpers are driven by imported data and should not be user-transformable.
            // Avoid activating the EntityTransformHandler so the gizmo doesn't appear (and to prevent accidental edits).
            if ((selection as any).transformLocked) {
                const name = String((selection as any).name ?? '');
                const label = name ? `"${name}"` : 'This element';
                events.fire(
                    'status.text',
                    `Animated helper ${label} is transform-locked. Select the *_root helper to align the import.`,
                    { timeoutMs: 3500 }
                );
                return;
            }

            // Splats have special multi-selection transform behavior
            if ((selection as any).numSelected !== undefined && (selection as any).numSelected > 0) {
                push(splatsTransformHandler);
            } else {
                push(entityTransformHandler);
            }
        }
    };

    const onSelectionChanged = (selection: any) => update(selection);
    const onSplatStateChanged = (_selection: any) => update(events.invoke('selection') as Splat | Mesh | Element);
    const onTransformHandlerPush = (handler: TransformHandler) => push(handler);
    const onTransformHandlerPop = () => pop();

    events.on('selection.changed', onSelectionChanged);
    events.on('splat.stateChanged', onSplatStateChanged);
    events.on('transformHandler.push', onTransformHandlerPush);
    events.on('transformHandler.pop', onTransformHandlerPop);

    // IMPORTANT: selection can be set before transform handlers register (e.g. GlobalClipBoxElement
    // is added during Scene construction and becomes the initial selection).
    // If so, we must bind the correct handler immediately so the first interaction works
    // even when the selection doesn't change on first click.
    update(events.invoke('selection') as Splat | Mesh | Element);

    const dispose = () => {
        events.off('selection.changed', onSelectionChanged);
        events.off('splat.stateChanged', onSplatStateChanged);
        events.off('transformHandler.push', onTransformHandlerPush);
        events.off('transformHandler.pop', onTransformHandlerPop);

        while (transformHandlers.length > 0) {
            pop();
        }

        (entityTransformHandler as any).destroy?.();
        (splatsTransformHandler as any).destroy?.();

        (events as any)._transformHandlersDispose = undefined;
    };

    (events as any)._transformHandlersDispose = dispose;
    return dispose;
};

export { registerTransformHandlerEvents, TransformHandler };
